
import { useState, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import baseUrls from '@/data/api-baseurl.json';

function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return (baseUrls as Record<string, string>)['default'];
  const hostname = window.location.hostname;
  return (baseUrls as Record<string, string>)[hostname] || (baseUrls as Record<string, string>)['default'];
}

type PageState = 'idle' | 'loading' | 'success' | 'error';

function UnsubscribeContent() {
  const [searchParams] = useSearchParams();
  const types = searchParams.get('types');
  const uid = searchParams.get('uid');
  const sig = searchParams.get('sig');
  const [state, setState] = useState<PageState>('idle');

  const isValid = types && uid && sig;

  const handleUnsubscribe = async () => {
    setState('loading');
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/v1/mailing-preferences/unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: uid, types, sig }),
      });
      if (!res.ok) throw new Error('Error');
      setState('success');
    } catch {
      setState('error');
    }
  };

  if (!isValid) {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-red-500 text-3xl">error_outline</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Enlace inválido</h2>
        <p className="text-gray-500 text-sm">Este enlace de desuscripción no es válido.</p>
      </div>
    );
  }

  if (state === 'success') {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-green-600 text-3xl">check_circle</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Listo</h2>
        <p className="text-gray-500 text-sm">Ya no recibirás estos recordatorios.</p>
        <a href="/login" className="inline-block text-sm font-medium text-blue-600 hover:text-blue-700 mt-2">
          Volver al inicio
        </a>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto">
          <span className="material-icons text-red-500 text-3xl">error_outline</span>
        </div>
        <h2 className="text-xl font-bold text-gray-900">Error</h2>
        <p className="text-gray-500 text-sm">No pudimos procesar tu solicitud. El enlace podría ser inválido.</p>
        <button onClick={() => setState('idle')} className="inline-block text-sm font-medium text-blue-600 hover:text-blue-700 mt-2">
          Intentar de nuevo
        </button>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6">
      <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
        <span className="material-icons text-blue-600 text-3xl">unsubscribe</span>
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-bold text-gray-900">Preferencias de email</h2>
        <p className="text-gray-500 text-sm">¿Deseas dejar de recibir recordatorios de configuración pendiente?</p>
      </div>
      <button
        onClick={handleUnsubscribe}
        disabled={state === 'loading'}
        className="w-full max-w-xs mx-auto flex justify-center py-3 px-6 border border-transparent text-sm font-bold rounded-xl text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {state === 'loading' ? 'Procesando...' : 'Desuscribirme de estos recordatorios'}
      </button>
    </div>
  );
}

export default function MailingUnsubscribePage() {
  return (
    <div className="light bg-white min-h-screen flex items-center justify-center p-4" style={{ colorScheme: 'light' }}>
      <div className="w-full max-w-sm space-y-8">
        <div className="flex items-center justify-center gap-2">
          <span className="material-icons text-blue-600 text-3xl">account_balance_wallet</span>
          <span className="font-bold text-2xl tracking-tight text-gray-900">FLUX</span>
        </div>
        <Suspense fallback={<div className="text-center text-gray-400 text-sm">Cargando...</div>}>
          <UnsubscribeContent />
        </Suspense>
      </div>
    </div>
  );
}
